/**
 * Keeping the Stytch session JWT fresh.
 *
 * The bug this exists for: sessions looked far shorter than they are. The app
 * asks Stytch for a seven-day session (SESSION_MINUTES) and gets one — but the
 * JWT *inside* that session is short-lived by design. Every request sent
 * whatever `session.getTokens()` had cached, and the API validates lifetime
 * with 30s of clock skew, so a backgrounded tab or a sleeping machine (both of
 * which throttle the SDK's refresh timer) meant the next call went out with an
 * expired JWT and came back 401 while the session was still perfectly valid.
 *
 * Two comments in the codebase asserted this could not happen — that the cached
 * token was "guaranteed fresh", and that there was "nothing useful to call".
 * The second was simply wrong: the installed SDK exposes
 * `session.authenticate({ session_duration_minutes })`, documented as extending
 * the session. That is what this module calls.
 *
 * Proactive, not just reactive. Refreshing only on a 401 means every wake-up
 * costs one failed request, and any caller that does not retry (SignalR's token
 * factory, for one) just fails. So the token is checked before it is used and
 * renewed when it is close to expiry; the 401 path stays as a backstop for the
 * cases this misses.
 *
 * Passing session_duration_minutes on refresh also makes the seven days a
 * sliding window rather than a hard deadline, so someone working all week is
 * not logged out mid-sentence on day seven.
 */
import { SESSION_MINUTES } from "@/views/auth/_shared/types";

/** Minimal shape we need — avoids depending on the SDK's generic client type. */
export interface StytchSessionLike {
  session: {
    getTokens: () => { session_jwt?: string | null } | null;
    authenticate: (opts?: { session_duration_minutes?: number }) => Promise<unknown>;
  };
}

/**
 * Renew when the JWT has less than this left. Comfortably more than the API's
 * 30s clock skew, so a token that passes this check cannot expire in flight on
 * a slow request.
 */
const MIN_LIFETIME_SECONDS = 60;

/**
 * Seconds until a JWT expires; null when it carries no readable `exp`.
 *
 * Deliberately not verified — this only decides whether to ask for a new token.
 * The API verifies signatures, and a malformed token here simply reads as
 * "refresh it", which is the safe direction to be wrong in.
 */
export function secondsUntilExpiry(jwt: string | null | undefined, now = Date.now()): number | null {
  if (!jwt) return null;
  const parts = jwt.split(".");
  if (parts.length < 2) return null;
  try {
    const payload = parts[1]!.replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, "=")));
    if (typeof json?.exp !== "number") return null;
    return json.exp - Math.floor(now / 1000);
  } catch {
    return null;
  }
}

export function needsRefresh(jwt: string | null | undefined, now = Date.now()): boolean {
  if (!jwt) return true;
  const left = secondsUntilExpiry(jwt, now);
  // No readable exp — leave it alone rather than hammering authenticate() on
  // every request for a token shape we do not understand.
  if (left === null) return false;
  return left < MIN_LIFETIME_SECONDS;
}

/**
 * One refresh at a time. Without this, a page that fires eight requests on
 * wake-up sends eight authenticate() calls, and the later ones race the earlier
 * ones' token writes.
 */
let inFlight: Promise<void> | null = null;

export function forceSessionRefresh(client: StytchSessionLike): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      await client.session.authenticate({ session_duration_minutes: SESSION_MINUTES });
    } catch {
      // Soft-fail: the caller still sends whatever it has and lets the API
      // decide. A refresh that cannot happen is not a reason to block a request
      // that might have succeeded anyway.
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/** Test seam — the single-flight latch is module state. */
export function __resetSessionRefreshForTests(): void {
  inFlight = null;
}

/**
 * The session JWT, renewed first if it is missing or close to expiry.
 * Returns undefined when there is no session at all.
 */
export async function freshSessionJwt(
  client: StytchSessionLike | null | undefined,
): Promise<string | undefined> {
  if (!client) return undefined;
  let jwt: string | undefined;
  try {
    jwt = client.session.getTokens()?.session_jwt ?? undefined;
  } catch {
    return undefined;
  }

  // No session at all is not a stale token — signing in is the caller's job.
  if (!jwt) return undefined;

  if (needsRefresh(jwt)) {
    await forceSessionRefresh(client);
    try {
      jwt = client.session.getTokens()?.session_jwt ?? jwt;
    } catch {
      /* keep what we had */
    }
  }
  return jwt;
}
